import React, { useContext, useState } from 'react';
import SETTING_KEYS from '../../constants/setting-keys';
import styles from './Procedure.module.css';
import ConditionalsTable from './conditionals/ConditionalsTable';
import ConditionalsComponent from './conditionals/ConditionalsComponent';
import ImplantInfoComponent from './info/ImplantInfoComponent';
import ImplantDetailsComponent from './info/ImplantDetailsComponent';
import AuthContext from '../../store/auth-context';
import RIGHTS_KEYS from '../../constants/rights-keys';
import ImplantImageComponent from './info/ImplantImageComponent';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import PolicyLink from './department-tools/PolicyLink';
import MRICompatibilityDialog from './mri-compatibility/MRICompatibilityDialog';
import GoToReportingButton from '../reports/GoToReportingButton';
import ApprovedComponent from '../approval/ApprovedComponent';
import { ImplantFoldersDisplay } from '../group/ImplantFoldersDisplay';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import ImplantNoteComponent from './notes/ImplantNoteComponent';
import BackgroundContainer from '../ui-components/BackgroundContainer';
import { ImplantPreviewDialog } from './preview/ImplantPreviewDialog';
import type { RegionalInfo } from '../../store/implant-context';

interface HistoryVersion {
    id: string;
    version: string;
    info: any;
}
interface ProcedureProps {
    info: any;
    conditionals: any;
    isPreview?: boolean;
    isPredefined?: boolean;
    historyVersions?: HistoryVersion[];
    selectedVersion?: HistoryVersion;
    handleChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    mriCode?: string;
    withDeptTools?: boolean;
}

const isEmpty = (value: any) => {
    return (
        value === undefined ||
        value === null ||
        value === '' ||
        value === '<p></p>'
    );
};

const Procedure: React.FC<ProcedureProps> = ({
    info,
    conditionals,
    isPreview = false,
    isPredefined = false,
    historyVersions,
    selectedVersion,
    handleChange,
    mriCode,
    withDeptTools = false,
}) => {
    const ctx = useContext(AuthContext);
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const [previewOpen, setPreviewOpen] = useState(false);

    const previewRegionalInfo: RegionalInfo = {
        regions: [],
        catalogNumbers: info[SETTING_KEYS.catalogNumbers] ?? [],
        mriStatus: info[SETTING_KEYS.mriStatus],
        ifu: info.ifu ?? '',
        image: info.image ?? '',
        otherFiles: info.otherFiles ?? [],
        customFields: info[SETTING_KEYS.customFields],
        precaution: conditionals?.[SETTING_KEYS.addInfo] ?? '',
        model: info[SETTING_KEYS.model],
    };

    return (
        <div className={!isPreview ? styles.implant : ''}>
            <div className={styles.infoWrapper}>
                <div className={styles.info}>
                    <ImplantInfoComponent
                        info={{
                            model: info[SETTING_KEYS.model],
                            manufacturer: info[SETTING_KEYS.manufacturer],
                            type: info[SETTING_KEYS.type],
                            mriStatus: info[SETTING_KEYS.mriStatus],
                            verifiedByNI: info[SETTING_KEYS.verifiedByNI],
                            mriCode: info.mriCode,
                            createdAt: info[SETTING_KEYS.createdAt],
                            filename: info[SETTING_KEYS.filename],
                        }}
                        isPreview={isPreview}
                        historyVersions={historyVersions}
                        selectedVersion={selectedVersion}
                        handleChange={handleChange}
                        onPreview={
                            !isPreview && withDeptTools
                                ? () => setPreviewOpen(true)
                                : undefined
                        }
                    />
                </div>
                <div className={styles.image}>
                    {ctx.orRights([RIGHTS_KEYS.detailedInfo]) && !isPreview && (
                        <ImplantImageComponent
                            name={info[SETTING_KEYS.image]}
                            file={info['image']}
                        />
                    )}
                </div>
            </div>
            <div className={styles.detailsWrapper}>
                {!isPreview && (
                    <div className={styles.deptTools}>
                        {ctx.orRights([RIGHTS_KEYS.deptTools]) && (
                            <BackgroundContainer>
                                <div className={styles.deptActions}>
                                    {withDeptTools && (
                                        <div className={styles.deptActions}>
                                            <ApprovedComponent
                                                implantId={info._id}
                                            />
                                            <ImplantFoldersDisplay
                                                implantId={info._id}
                                            />
                                            <PolicyLink
                                                implantId={info._id}
                                            />
                                            {info[SETTING_KEYS.mriStatus] ===
                                                2 && (
                                                <MRICompatibilityDialog
                                                    conditionalId={
                                                        conditionals?._id ??
                                                        null
                                                    }
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </BackgroundContainer>
                        )}
                    </div>
                )}
                <div className={styles.details}>
                    <ImplantDetailsComponent
                        info={{
                            model: info[SETTING_KEYS.model],
                            otherFiles: info.otherFiles,
                            category: info[SETTING_KEYS.category],
                            distEndDate: info[SETTING_KEYS.distEndDate],
                            bodyPlacement: info[SETTING_KEYS.bodyPlacement],
                            catalogNumbers: info[SETTING_KEYS.catalogNumbers],
                            maxStentLength: info[SETTING_KEYS.maxStentLength],
                            gmdnDefinition: info[SETTING_KEYS.gmdnDefinition],
                            drugType: info[SETTING_KEYS.drugType],
                        }}
                        hasToggle={!isPreview}
                    />
                    {withDeptTools && (
                        <div style={{ marginBottom: '30px' }}>
                            <ImplantNoteComponent implantId={info._id} />
                        </div>
                    )}
                    {info[SETTING_KEYS.customFields]?.some(
                        (field: any) => field.isCritical
                    ) &&
                        info[SETTING_KEYS.customFields]
                            .filter((field: any) => field.isCritical)
                            .map((field: any) => {
                                if (
                                    field === undefined ||
                                    field === null ||
                                    field === ''
                                ) {
                                    return '';
                                }
                                return (
                                    <div className={styles.customField}>
                                        <ConditionalsTable
                                            style="critical"
                                            headers={[
                                                <>
                                                    <ReportGmailerrorredIcon />
                                                    {field.heading}
                                                </>,
                                            ]}
                                            fields={[[field.value]]}
                                        />
                                    </div>
                                );
                            })}
                    {conditionals[SETTING_KEYS.addInfo] &&
                        !isEmpty(conditionals[SETTING_KEYS.addInfo]) && (
                            <div style={{ marginBottom: '30px' }}>
                                <ConditionalsTable
                                    isInfo
                                    title={SETTING_KEYS.addInfo}
                                    style="info"
                                    headers={[
                                        <>
                                            <ReportGmailerrorredIcon />
                                            {SETTING_KEYS.addInfo}
                                        </>,
                                    ]}
                                    fields={[
                                        [
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: conditionals[
                                                        SETTING_KEYS.addInfo
                                                    ] as string,
                                                }}
                                            ></div>,
                                        ],
                                    ]}
                                />
                            </div>
                        )}
                    {info[SETTING_KEYS.mriStatus] === 2 && (
                        <ConditionalsComponent conditionals={conditionals} />
                    )}

                    {!isEmpty(info[SETTING_KEYS.notes]) && (
                        <div className={styles.customField}>
                            <ConditionalsTable
                                headers={[SETTING_KEYS.notes]}
                                fields={[[info[SETTING_KEYS.notes]]]}
                            />
                        </div>
                    )}
                    {info[SETTING_KEYS.customFields]?.some(
                        (field: any) => !field.isCritical
                    ) &&
                        info[SETTING_KEYS.customFields]
                            .filter((field: any) => !field.isCritical)
                            .map((field: any) => {
                                if (isEmpty(field)) {
                                    return '';
                                }
                                return (
                                    <div className={styles.customField}>
                                        <ConditionalsTable
                                            headers={[field.heading]}
                                            fields={[[field.value]]}
                                        />
                                    </div>
                                );
                            })}
                </div>
            </div>
            <ImplantPreviewDialog
                open={previewOpen}
                onOpenChange={setPreviewOpen}
                info={info}
                regionalInfo={previewRegionalInfo}
                conditionals={conditionals}
            />
        </div>
    );
};

export default Procedure;
